/**
 * Admin infrastructure visualization page.
 * Uses React Flow to render an interactive architecture diagram of the Nara platform.
 * Data is fetched from the Cloud Run Admin API via Firebase Functions.
 *
 * Supports two view modes:
 * - Overview: all service nodes grouped by layer
 * - Detail: drill-down into a service showing sub-services as graph nodes
 */

import { useState, useMemo, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  Node,
  Edge,
  Handle,
  Position,
  NodeProps,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { PageHeader } from "../components/layout/PageHeader";
import { useProfile } from "../contexts/ProfileContext";
import {
  useAdminInfrastructure,
  useAdminRefreshInfrastructure,
} from "../hooks/useAdminInfrastructure";
import type {
  InfrastructureNode as ApiNode,
  InfrastructureEdge as ApiEdge,
} from "../services/naraDoor";
import {
  Monitor,
  Smartphone,
  Server,
  Database,
  Cloud,
  Mail,
  MessageSquare,
  Brain,
  FileText,
  HardDrive,
  Workflow,
  Lock,
  Zap,
  RefreshCw,
  Loader2,
} from "lucide-react";

// ─── Icon mapping ─────────────────────────────────────────────────────────────

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  Monitor,
  Smartphone,
  Server,
  Database,
  Cloud,
  Mail,
  MessageSquare,
  Brain,
  FileText,
  HardDrive,
  Workflow,
  Lock,
  Zap,
};

// ─── Layer color schemes ───────────────────────────────────────────────────────

const LAYER_COLORS = {
  client: {
    bg: "bg-blue-500/15",
    border: "border-blue-500/40",
    badge: "bg-blue-500/20 text-blue-400",
    accent: "text-blue-400",
  },
  gateway: {
    bg: "bg-orange-500/15",
    border: "border-orange-500/40",
    badge: "bg-orange-500/20 text-orange-400",
    accent: "text-orange-400",
  },
  http: {
    bg: "bg-emerald-500/15",
    border: "border-emerald-500/40",
    badge: "bg-emerald-500/20 text-emerald-400",
    accent: "text-emerald-400",
  },
  async: {
    bg: "bg-purple-500/15",
    border: "border-purple-500/40",
    badge: "bg-purple-500/20 text-purple-400",
    accent: "text-purple-400",
  },
  infra: {
    bg: "bg-slate-500/15",
    border: "border-slate-500/40",
    badge: "bg-slate-500/20 text-slate-400",
    accent: "text-slate-400",
  },
} as const;

type LayerType = keyof typeof LAYER_COLORS;

// ─── Custom node data types ────────────────────────────────────────────────────

type InfraNodeData = {
  label: string;
  description?: string;
  subServices?: { name: string; description: string; capabilities: string[] }[];
  layer: LayerType;
  icon: React.ComponentType<{ className?: string }>;
  runtime?: {
    revision?: string;
    memory?: string;
    cpu?: string;
    maxInstances?: number;
    lastDeploy?: string;
    status?: string;
  };
  [key: string]: unknown;
};

type InfraNode = Node<InfraNodeData>;

type SubServiceNodeData = {
  name: string;
  description: string;
  capabilities: string[];
  parentLayer: LayerType;
  [key: string]: unknown;
};

type SubServiceNode = Node<SubServiceNodeData>;

// ─── Custom Node Components ───────────────────────────────────────────────────

function InfraNodeComponent({ data }: NodeProps<InfraNode>) {
  const colors = LAYER_COLORS[data.layer];
  const Icon = data.icon;

  // Format relative time for last deploy
  const lastDeployLabel = useMemo(() => {
    if (!data.runtime?.lastDeploy) return null;
    const deployed = new Date(data.runtime.lastDeploy);
    const now = new Date();
    const diffMs = now.getTime() - deployed.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return "today";
    if (diffDays === 1) return "1d ago";
    return `${diffDays}d ago`;
  }, [data.runtime?.lastDeploy]);

  return (
    <div
      className={`rounded-xl border ${colors.bg} ${colors.border} px-4 py-3 min-w-[180px] max-w-[220px] shadow-lg`}
    >
      <Handle
        type="target"
        position={Position.Top}
        className="!bg-muted-foreground/50 !w-2 !h-2 !border-0"
      />

      <div className="flex items-center gap-2 mb-1">
        <Icon className={`h-4 w-4 shrink-0 ${colors.accent}`} />
        <span className="text-sm font-semibold text-foreground leading-tight">
          {data.label}
        </span>
      </div>

      {data.description && (
        <div className="text-[10px] text-muted-foreground/80 leading-tight pl-6 mb-1 italic">
          {data.description}
        </div>
      )}

      {data.subServices && data.subServices.length > 0 && (
        <div className="mt-1.5 space-y-0.5">
          {data.subServices.map((sub) => (
            <div
              key={sub.name}
              className="text-[10px] text-muted-foreground leading-tight pl-6"
            >
              {sub.name}
            </div>
          ))}
        </div>
      )}

      {data.runtime && (
        <div className="mt-2 pt-1.5 border-t border-muted-foreground/10 space-y-0.5">
          {data.runtime.revision && (
            <div className="text-[9px] text-muted-foreground/60 pl-6 font-mono">
              {data.runtime.revision}
            </div>
          )}
          <div className="flex gap-2 pl-6">
            {data.runtime.memory && (
              <span className="text-[9px] text-muted-foreground/60">
                {data.runtime.memory}
              </span>
            )}
            {data.runtime.cpu && (
              <span className="text-[9px] text-muted-foreground/60">
                {data.runtime.cpu} CPU
              </span>
            )}
            {lastDeployLabel && (
              <span className="text-[9px] text-muted-foreground/60">
                {lastDeployLabel}
              </span>
            )}
          </div>
        </div>
      )}

      <Handle
        type="source"
        position={Position.Bottom}
        className="!bg-muted-foreground/50 !w-2 !h-2 !border-0"
      />
    </div>
  );
}

function SubServiceNodeComponent({ data }: NodeProps<SubServiceNode>) {
  const colors = LAYER_COLORS[data.parentLayer];

  return (
    <div
      className={`rounded-xl border ${colors.bg} ${colors.border} px-4 py-3 min-w-[220px] max-w-[260px] shadow-lg`}
    >
      <Handle
        type="target"
        position={Position.Top}
        className="!bg-muted-foreground/50 !w-2 !h-2 !border-0"
      />

      <div className="text-sm font-semibold text-foreground mb-0.5">
        {data.name}
      </div>

      {data.description && (
        <div className="text-[10px] text-muted-foreground/80 italic mb-2">
          {data.description}
        </div>
      )}

      {data.capabilities.length > 0 && (
        <ul className="space-y-0.5">
          {data.capabilities.map((cap, i) => (
            <li
              key={i}
              className="text-[10px] text-muted-foreground flex gap-1.5"
            >
              <span
                className={`mt-1 shrink-0 h-1 w-1 rounded-full ${colors.accent} bg-current`}
              />
              {cap}
            </li>
          ))}
        </ul>
      )}

      <Handle
        type="source"
        position={Position.Bottom}
        className="!bg-muted-foreground/50 !w-2 !h-2 !border-0"
      />
    </div>
  );
}

const nodeTypes = {
  infra: InfraNodeComponent,
  subservice: SubServiceNodeComponent,
};

// ─── Layout engine ────────────────────────────────────────────────────────────

const CENTER_X = 600;

function buildNodesFromData(apiNodes: ApiNode[]): InfraNode[] {
  // Group by layer
  const layers: Record<string, ApiNode[]> = {};
  for (const node of apiNodes) {
    const layer = node.layer || "http";
    if (!layers[layer]) layers[layer] = [];
    layers[layer].push(node);
  }

  const layerOrder: LayerType[] = [
    "client",
    "gateway",
    "http",
    "async",
    "infra",
  ];
  const layerY: Record<string, number> = {
    client: 0,
    gateway: 200,
    http: 430,
    async: 700,
    infra: 950,
  };

  const result: InfraNode[] = [];

  for (const layer of layerOrder) {
    const nodes = layers[layer] || [];
    if (nodes.length === 0) continue;

    // Calculate gap based on layer
    const gap = layer === "infra" ? 170 : layer === "client" ? 350 : 260;
    const totalWidth = (nodes.length - 1) * gap;

    nodes.forEach((node, index) => {
      const x = CENTER_X - totalWidth / 2 + index * gap;
      const IconComponent = ICON_MAP[node.icon] || Server;

      result.push({
        id: node.id,
        type: "infra",
        position: { x, y: layerY[layer] ?? 430 },
        data: {
          label: node.label,
          description: node.description,
          subServices: node.subServices,
          layer: layer as LayerType,
          icon: IconComponent,
          runtime: node.runtime
            ? {
                revision: node.runtime.revision,
                memory: node.runtime.memory,
                cpu: node.runtime.cpu,
                maxInstances: node.runtime.maxInstances,
                lastDeploy: node.runtime.lastDeploy,
                status: node.runtime.status,
              }
            : undefined,
        },
      });
    });
  }

  return result;
}

const EDGE_STYLE = { stroke: "rgba(148, 163, 184, 0.3)", strokeWidth: 1.5 };
const EDGE_STYLE_HIGHLIGHT = {
  stroke: "rgba(148, 163, 184, 0.5)",
  strokeWidth: 2,
};

function buildEdgesFromData(apiEdges: ApiEdge[]): Edge[] {
  return apiEdges.map((edge) => ({
    id: `e-${edge.source}-${edge.target}`,
    source: edge.source,
    target: edge.target,
    style:
      edge.type === "client-gateway" ? EDGE_STYLE_HIGHLIGHT : EDGE_STYLE,
  }));
}

// ─── Detail view builders ────────────────────────────────────────────────────

function buildDetailNodes(
  serviceNode: ApiNode
): (InfraNode | SubServiceNode)[] {
  const IconComponent = ICON_MAP[serviceNode.icon] || Server;
  const layer = (serviceNode.layer || "http") as LayerType;
  const subs = serviceNode.subServices || [];

  const gap = 280;
  const totalWidth = (subs.length - 1) * gap;

  const nodes: (InfraNode | SubServiceNode)[] = [
    // Parent node at top center
    {
      id: serviceNode.id,
      type: "infra",
      position: { x: CENTER_X - 100, y: 0 },
      data: {
        label: serviceNode.label,
        description: serviceNode.description,
        subServices: serviceNode.subServices,
        layer,
        icon: IconComponent,
        runtime: serviceNode.runtime
          ? { ...serviceNode.runtime }
          : undefined,
      },
    },
  ];

  // Sub-service nodes in a row below
  subs.forEach((sub, index) => {
    nodes.push({
      id: `${serviceNode.id}-${sub.name}`,
      type: "subservice",
      position: {
        x: CENTER_X - totalWidth / 2 + index * gap,
        y: 250,
      },
      data: {
        name: sub.name,
        description: sub.description,
        capabilities: sub.capabilities || [],
        parentLayer: layer,
      },
    });
  });

  return nodes;
}

function buildDetailEdges(serviceNode: ApiNode): Edge[] {
  const subs = serviceNode.subServices || [];
  return subs.map((sub) => ({
    id: `e-${serviceNode.id}-${sub.name}`,
    source: serviceNode.id,
    target: `${serviceNode.id}-${sub.name}`,
    style: EDGE_STYLE,
  }));
}

// ─── Time ago helper ──────────────────────────────────────────────────────────

function timeAgo(isoString: string): string {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

// ─── Legend Component ──────────────────────────────────────────────────────────

function Legend() {
  const layers: { label: string; layer: LayerType }[] = [
    { label: "Clients", layer: "client" },
    { label: "Gateway / BFF", layer: "gateway" },
    { label: "HTTP Services", layer: "http" },
    { label: "Async / Workflows", layer: "async" },
    { label: "Infrastructure", layer: "infra" },
  ];

  return (
    <div className="absolute top-3 left-3 z-10 flex flex-wrap gap-2">
      {layers.map(({ label, layer }) => {
        const colors = LAYER_COLORS[layer];
        return (
          <span
            key={layer}
            className={`text-[11px] font-medium px-2.5 py-1 rounded-full ${colors.badge}`}
          >
            {label}
          </span>
        );
      })}
    </div>
  );
}

// ─── Breadcrumb Component ─────────────────────────────────────────────────────

function Breadcrumb({
  serviceName,
  onBack,
}: {
  serviceName: string;
  onBack: () => void;
}) {
  return (
    <div className="absolute top-3 left-3 z-10 flex items-center gap-1.5 text-xs">
      <button
        onClick={onBack}
        className="text-muted-foreground hover:text-foreground hover:underline transition-colors"
      >
        Infrastructure
      </button>
      <span className="text-muted-foreground/50">/</span>
      <span className="text-foreground font-medium">{serviceName}</span>
    </div>
  );
}

// ─── Flow Component ────────────────────────────────────────────────────────────

function InfraFlow() {
  const { data, isLoading } = useAdminInfrastructure();
  const [selectedServiceId, setSelectedServiceId] = useState<string | null>(
    null
  );

  // Find the selected service's API data
  const selectedApiNode = selectedServiceId
    ? data?.nodes.find((n) => n.id === selectedServiceId)
    : null;

  // Overview nodes/edges
  const overviewNodes = useMemo(
    () => (data?.nodes ? buildNodesFromData(data.nodes) : []),
    [data?.nodes]
  );
  const overviewEdges = useMemo(
    () => (data?.edges ? buildEdgesFromData(data.edges) : []),
    [data?.edges]
  );

  // Detail nodes/edges
  const detailNodes = useMemo(
    () => (selectedApiNode ? buildDetailNodes(selectedApiNode) : []),
    [selectedApiNode]
  );
  const detailEdges = useMemo(
    () => (selectedApiNode ? buildDetailEdges(selectedApiNode) : []),
    [selectedApiNode]
  );

  const isDetailView = !!selectedServiceId && !!selectedApiNode;
  const nodes = (isDetailView ? detailNodes : overviewNodes) as Node[];
  const edges = isDetailView ? detailEdges : overviewEdges;

  const onNodeClick = useCallback(
    (_: React.MouseEvent, node: Node) => {
      // Only drill down from overview, not from detail view
      if (!selectedServiceId) {
        const apiNode = data?.nodes.find((n) => n.id === node.id);
        const hasCaps = apiNode?.subServices?.some(
          (s) => s.capabilities?.length > 0
        );
        if (hasCaps) {
          setSelectedServiceId(node.id);
        }
      }
    },
    [selectedServiceId, data?.nodes]
  );

  const handleBack = useCallback(() => {
    setSelectedServiceId(null);
  }, []);

  if (isLoading) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!data || data.nodes.length === 0) {
    return (
      <div className="w-full h-full flex items-center justify-center text-muted-foreground">
        <div className="text-center">
          <p className="text-lg font-medium">No infrastructure data</p>
          <p className="text-sm mt-1">
            Click Refresh to discover services from Cloud Run
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full relative">
      {isDetailView ? (
        <Breadcrumb serviceName={selectedApiNode.label} onBack={handleBack} />
      ) : (
        <Legend />
      )}
      <ReactFlow
        key={selectedServiceId || "overview"}
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        onNodeClick={onNodeClick}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={true}
        panOnScroll
        minZoom={0.3}
        maxZoom={1.5}
        proOptions={{ hideAttribution: true }}
      >
        <Background gap={20} size={1} />
        <Controls showInteractive={false} />
      </ReactFlow>
    </div>
  );
}

// ─── Page Component ────────────────────────────────────────────────────────────

export function AdminInfrastructure() {
  const { isAdmin } = useProfile();
  const { t } = useTranslation();
  const { data } = useAdminInfrastructure();
  const refreshMutation = useAdminRefreshInfrastructure();

  if (!isAdmin) return <Navigate to="/" replace />;

  return (
    <>
      <PageHeader
        title={t("admin.infrastructure")}
        actions={
          <div className="flex items-center gap-3">
            {data?.lastRefreshed && (
              <span className="text-xs text-muted-foreground">
                Updated {timeAgo(data.lastRefreshed)}
              </span>
            )}
            <button
              onClick={() => refreshMutation.mutate()}
              disabled={refreshMutation.isPending}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-primary/10 text-primary hover:bg-primary/20 disabled:opacity-50 transition-colors"
            >
              {refreshMutation.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <RefreshCw className="h-3.5 w-3.5" />
              )}
              Refresh
            </button>
          </div>
        }
      />
      <div style={{ height: "calc(100vh - 140px)" }}>
        <ReactFlowProvider>
          <InfraFlow />
        </ReactFlowProvider>
      </div>
    </>
  );
}
